import React from "react";
import { ParallaxProvider } from "react-scroll-parallax";
import { motion } from "framer-motion";

import Header from "./components/Header";
import Hero from "./components/Hero";
import About from "./components/About";
import Timeline from "./components/Timeline";
import LifeInMotion from "./components/LifeInMotion";
import Speaking from "./components/Speaking";
import Philosophy from "./components/Philosophy";
import Connect from "./components/Connect";
import Footer from "./components/Footer";

const FloatingCircle = ({ className, size = 200, duration = 20, delay = 0 }) => (
  <motion.div
    className={`absolute ${className}`}
    style={{ width: size, height: size }}
    animate={{ rotate: 360 }}
    transition={{ repeat: Infinity, duration, ease: "linear", delay }}
  >
    <svg viewBox="0 0 200 200" fill="currentColor" className="w-full h-full opacity-20">
      <circle cx="100" cy="100" r="100" />
    </svg>
  </motion.div>
);

function App() {
  return (
    <ParallaxProvider>
      {/* Animated background SVG circles */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <FloatingCircle className="top-10 left-0 text-green-100" duration={30} size={300} />
        <FloatingCircle className="bottom-20 right-0 text-green-200" duration={40} size={400} delay={5} />
        <FloatingCircle className="top-1/2 left-1/2 text-green-50 transform -translate-x-1/2 -translate-y-1/2" duration={50} size={250} delay={2} />
        <FloatingCircle className="top-1/3 right-1/4 text-green-200" duration={35} size={180} delay={1} />
      </div>

      <div className="relative">
        <Header />

        {/* Hero Section with subtle gradient overlay */}
        <motion.section
          id="hero"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative bg-gradient-to-b from-green-50 via-white to-green-50"
        >
          <Hero />
        </motion.section>

        {/* About Section with gradient shape */}
        <motion.section
          id="about"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="relative bg-gradient-to-b from-white via-green-50 to-white overflow-hidden"
        >
          {/* Subtle decorative shape */}
          <div className="absolute -top-32 left-0 w-64 h-64 bg-green-100 rounded-full opacity-10 -z-10" />
          <About />
        </motion.section>

        {/* Timeline Section */}
        <motion.section
          id="timeline"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          viewport={{ once: true }}
          className="relative bg-gradient-to-b from-green-50 via-white to-green-50 overflow-hidden"
        >
          <div className="absolute -bottom-20 right-0 w-64 h-64 bg-green-200 rounded-full opacity-10 -z-10" />
          <Timeline />
        </motion.section>

        {/* Life in Motion Section */}
        <motion.section
          id="motion"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="relative bg-white overflow-hidden"
        >
          <div className="absolute top-0 left-1/4 w-48 h-48 bg-green-50 rounded-full opacity-10 -z-10" />
          <LifeInMotion />
        </motion.section>

        {/* Speaking Section with hover scaling for cards */}
        <motion.section
          id="speaking"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
          className="relative bg-gradient-to-b from-green-50 via-white to-green-50 overflow-hidden"
        >
          <Speaking />
        </motion.section>

        {/* Philosophy Section */}
        <motion.section
          id="philosophy"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="relative bg-green-600 overflow-hidden text-white"
        >
          <Philosophy />
        </motion.section>

        {/* Connect Section */}
        <motion.section
          id="connect"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="relative bg-gradient-to-b from-white via-green-50 to-white overflow-hidden"
        >
          <div className="absolute -bottom-20 left-0 w-64 h-64 bg-green-100 rounded-full opacity-10 -z-10" />
          <Connect />
        </motion.section>

        <Footer />
      </div>
    </ParallaxProvider>
  );
}

export default App;